// ===========================================================================
// Provider Documentation Coach Service (Módulo F — BillingV2)
//
// TODO BACKEND: métodos con datos fake (services/fakeData.js) y latencia
// simulada. Cuando el schema `analytics` exista, reemplazar por axios real
// (src/utils/Apis/MCMCore/axios.js) contra /api/analytics/*.
// ===========================================================================

import { FAKE_NOTES, FAKE_PROVIDER_SCORES } from "./fakeData";
import { NOTE_STATUS } from "../common/constants";

const SIMULATED_LATENCY_MS = 350;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Obtiene la cola de notas cerradas con hallazgos de documentación.
 *
 * Endpoint futuro:
 * GET /api/analytics/doc-feedback?status=open&providerId={id}
 *
 * Respuesta esperada (200): array de hallazgos por nota
 * (analytics.TAB_DOC_FEEDBACK), cada uno con issues NLP detectados
 * (missing MEAT, unspecified code, missing linkage) y sugerencia de código.
 *
 * @returns {Promise<Object[]>}
 */
export const getNotesWithFindings = async () => {
  await delay(SIMULATED_LATENCY_MS);
  return FAKE_NOTES.map((n) => ({ ...n }));
};

/**
 * Obtiene el scorecard de calidad documental por provider.
 *
 * Endpoint futuro:
 * GET /api/analytics/provider-doc-scores?period=YYYY-MM
 *
 * Respuesta esperada (200): array de analytics.TAB_PROVIDER_DOC_SCORE con
 * specificity score, MEAT completeness y HCC perdidos por PCP.
 *
 * @returns {Promise<Object[]>}
 */
export const getProviderScores = async () => {
  await delay(SIMULATED_LATENCY_MS);
  return FAKE_PROVIDER_SCORES.map((p) => ({ ...p }));
};

/**
 * Marca una nota como "coaching enviado" al provider.
 *
 * Endpoint futuro:
 * PUT /api/analytics/doc-feedback/{noteId}/send-coaching
 * Body: { sentBy: number }
 *
 * Respuesta esperada (200): { "result": 0, "message": "Coaching sent" }
 * Posibles errores (400): { "result": 1, "message": "Feedback not found" }
 *
 * @param {string} noteId
 * @returns {Promise<{result: number, status: string}>}
 */
export const sendCoaching = async (noteId) => {
  await delay(200);
  // Local-only hasta que exista backend.
  return { result: 0, status: NOTE_STATUS.COACHED, noteId };
};

/**
 * Métricas para la Signal Bar a partir de las notas cargadas.
 * (Backend futuro: GET /api/analytics/doc-feedback/summary)
 *
 * @param {Object[]} notes
 * @param {Object[]} providerScores
 */
export const buildCoachSignals = (notes = [], providerScores = []) => {
  const avg = (arr) =>
    arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
  return {
    flaggedNotes: notes.filter((n) => n.issues.length > 0).length,
    pendingReview: notes.filter((n) => n.status === NOTE_STATUS.OPEN).length,
    specificity: avg(providerScores.map((p) => p.specificity)),
    meat: avg(providerScores.map((p) => p.meat)),
    missedHcc: providerScores.reduce((acc, p) => acc + p.missedHcc, 0),
  };
};
